import Component from "@glimmer/component";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { buildBBCodeAttrs } from "discourse/lib/text";
import DButton from "discourse/ui-kit/d-button";
import { i18n } from "discourse-i18n";
import pollBounds from "discourse/plugins/poll/lib/poll-bounds";
import PollUiBuilder from "./modal/poll-ui-builder";
import PollInfo from "./poll-info";

export default class PollNodeView extends Component {
  @service modal;

  constructor() {
    super(...arguments);
    this.args.contentDOM.classList.add(
      "poll-container",
      "composer-poll-node__content"
    );
    this.args.dom.style.setProperty(
      "--poll-title-placeholder",
      JSON.stringify(i18n("poll.ui_builder.poll_title.placeholder"))
    );
    this.args.onSetup(this);
    this.update(this.args.node);
  }

  get #optionCount() {
    return this.#optionList(this.args.node)?.node.childCount ?? 0;
  }

  get #bounds() {
    return pollBounds(this.args.node.attrs, this.#optionCount);
  }

  get pollInfo() {
    const { attrs } = this.args.node;
    const closesAt = attrs.close ? moment(attrs.close) : null;

    return {
      isMultiple: attrs.type === "multiple",
      isPublic: attrs.public === "true",
      isDynamic: attrs.dynamic === "true",
      results: attrs.results,
      min: this.#bounds.min,
      max: this.#bounds.max,
      closesAt,
      isAutomaticallyClosed: !!closesAt?.isBefore(moment()),
      // PollInfo uses only the length when describing selection limits.
      options: new Array(this.#optionCount),
    };
  }

  deselectNode() {
    this.args.dom.classList.remove("ProseMirror-selectednode");
  }

  @action
  editPoll() {
    const { node } = this.args;

    this.modal.show(PollUiBuilder, {
      model: {
        poll: { ...node.attrs, optionCount: this.#optionCount },
        onSave: (attrs) => this.#savePoll(attrs),
      },
    });
  }

  ignoreMutation(mutation) {
    return (
      mutation.type !== "selection" &&
      ((mutation.type === "attributes" &&
        mutation.target === this.args.contentDOM) ||
        !this.args.contentDOM.contains(mutation.target))
    );
  }

  selectNode() {
    this.args.dom.classList.add("ProseMirror-selectednode");
  }

  stopEvent(event) {
    return !!event.target.closest(".composer-poll-node__edit");
  }

  update(node) {
    const attrs = node.type.spec.toDOM(node)[1];
    for (const [name, value] of Object.entries(attrs)) {
      if (name === "class") {
        this.args.dom.classList.add(value);
      } else if (value === null) {
        this.args.dom.removeAttribute(name);
      } else {
        this.args.dom.setAttribute(name, value);
      }
    }
    // An empty editor block contains a trailing break, so :empty won't match.
    this.args.dom.classList.toggle(
      "--untitled",
      node.firstChild?.type.name === "poll_title" &&
        node.firstChild.content.size === 0
    );

    // Generated options are omitted from Markdown, so edits here would be lost.
    const list = this.args.contentDOM.querySelector(":scope > ul");
    if (list) {
      if (node.attrs.type === "number") {
        list.contentEditable = "false";
      } else {
        list.removeAttribute("contenteditable");
      }
    }
  }

  #optionList(node) {
    const { bullet_list: bulletList } = this.args.pluginParams.schema.nodes;
    let offset = 1;

    for (let index = 0; index < node.childCount; index++) {
      const child = node.child(index);
      if (child.type === bulletList) {
        return { node: child, offset };
      }
      offset += child.nodeSize;
    }
  }

  #savePoll(attrs) {
    const { getPos, view, pluginParams } = this.args;
    const pos = getPos();
    if (pos === undefined || view.isDestroyed) {
      return;
    }

    const node = view.state.doc.nodeAt(pos);
    if (node?.type !== this.args.node.type) {
      return;
    }

    const tr = view.state.tr.setNodeMarkup(pos, null, attrs);

    if (attrs.type === "number") {
      const generatedPoll = pluginParams.utils.convertFromMarkdown(
        `[poll ${buildBBCodeAttrs(attrs)}]\n[/poll]`
      ).firstChild;
      const generated = generatedPoll && this.#optionList(generatedPoll)?.node;
      const list = this.#optionList(node);

      if (list && generated?.type === list.node.type) {
        const from = pos + list.offset;
        tr.replaceWith(from, from + list.node.nodeSize, generated);
      }
    }

    tr.setSelection(pluginParams.pmState.NodeSelection.create(tr.doc, pos));
    view.dispatch(tr);
    view.focus();
  }

  <template>
    <div class="composer-poll-node__info" contenteditable="false">
      {{#let this.pollInfo as |info|}}
        <PollInfo
          @closesAt={{info.closesAt}}
          @isAutomaticallyClosed={{info.isAutomaticallyClosed}}
          @isDynamic={{info.isDynamic}}
          @isMultiple={{info.isMultiple}}
          @isPublic={{info.isPublic}}
          @max={{info.max}}
          @min={{info.min}}
          @options={{info.options}}
          @results={{info.results}}
          @voters={{0}}
        />
      {{/let}}
    </div>
    <div
      class="poll-buttons composer-poll-node__actions"
      contenteditable="false"
    >
      <DButton
        class="btn-default composer-poll-node__edit"
        @action={{this.editPoll}}
        @icon="pencil"
        @label="poll.ui_builder.edit"
        @preventFocus={{true}}
      />
    </div>
  </template>
}
